/**
 * useDarkMode — iter-213.8
 *
 * High-contrast dark theme toggle. Persists the user's choice in
 * localStorage and applies the `dark` class on `<html>` so Tailwind's
 * `darkMode: "class"` variants kick in (tailwind.config.js).
 *
 * Returns `[isDark, toggle]`. The initial value reads localStorage
 * once on mount; subsequent toggles write back. If nothing is saved,
 * we default to LIGHT (the existing UX) — there is no auto-detect
 * via `prefers-color-scheme` because the financial dashboard has
 * dense, color-coded data where users want explicit control.
 */
import { useCallback, useEffect, useState } from "react";

const LS_KEY = "lumfi:theme";

function readInitial() {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(LS_KEY) === "dark";
  } catch {
    return false;
  }
}

function apply(isDark) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (isDark) root.classList.add("dark");
  else root.classList.remove("dark");
}

export function useDarkMode() {
  const [isDark, setIsDark] = useState(readInitial);

  useEffect(() => {
    apply(isDark);
    try {
      window.localStorage.setItem(LS_KEY, isDark ? "dark" : "light");
    } catch {
      // disabled storage — no-op
    }
  }, [isDark]);

  const toggle = useCallback(() => setIsDark((v) => !v), []);

  return [isDark, toggle];
}
